import AccessTime from "@mui/icons-material/AccessTime";
import MoreHoriz from "@mui/icons-material/MoreHoriz";
import StopCircleOutlinedIcon from "@mui/icons-material/StopCircleOutlined";
import { Box, Chip, IconButton, Typography } from "@mui/material";
import { useState, type MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import type { ChatSessionSummary } from "@/features/chat/model/chatHistoryTypes";
import { getChatSessionDisplayTitle } from "@/features/chat/model/filterChatSessions";
import { ChatHistoryCardMenu } from "@/features/chat/ui/ChatHistoryCardMenu";
import { formatRelativeDateTime } from "@/shared/lib/formatRelativeDateTime";

type ChatHistoryCardProps = {
  session: ChatSessionSummary;
  untitledLabel: string;
  terminatedLabel: string;
  menuAriaLabel: string;
  editTitleLabel: string;
  deleteLabel: string;
  onEditTitle: (session: ChatSessionSummary) => void;
  onDelete: (session: ChatSessionSummary) => void;
};

export function ChatHistoryCard({
  session,
  untitledLabel,
  terminatedLabel,
  menuAriaLabel,
  editTitleLabel,
  deleteLabel,
  onEditTitle,
  onDelete,
}: ChatHistoryCardProps) {
  const navigate = useNavigate();
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const displayTitle = getChatSessionDisplayTitle(session, untitledLabel);
  const formattedDate = formatRelativeDateTime(session.createdAt);

  const handleMenuOpen = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCardClick = () => {
    navigate(`/chat/${session.sessionId}`);
  };

  return (
    <>
      <Box
        onClick={handleCardClick}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleCardClick();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={displayTitle}
        sx={{
          display: "flex",
          flexDirection: "column",
          p: 2.5,
          borderRadius: 2,
          bgcolor: "var(--theme-surface)",
          border: "1px solid var(--theme-border)",
          cursor: "pointer",
          transition: "border-color 0.15s ease",
          "&:hover": {
            borderColor: "var(--theme-primary)",
          },
          "&:focus-visible": {
            outline: "2px solid var(--theme-primary)",
            outlineOffset: 2,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Typography
            variant="subtitle1"
            component="h3"
            sx={{
              fontWeight: 700,
              color: "var(--theme-text-primary)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
              minWidth: 0,
            }}
          >
            {displayTitle}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexShrink: 0,
            }}
          >
            {session.hasTerminatedTurn ? (
              <Chip
                label={terminatedLabel}
                size="small"
                color="warning"
                icon={<StopCircleOutlinedIcon sx={{ fontSize: "14px !important" }} />}
                sx={{ flexShrink: 0 }}
              />
            ) : null}
            {session.domainName ? (
              <Typography
                variant="caption"
                component="span"
                sx={{
                  color: "var(--theme-text-secondary)",
                  flexShrink: 0,
                  whiteSpace: "nowrap",
                  maxWidth: 180,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {session.domainName}
              </Typography>
            ) : null}
            <IconButton
              size="small"
              aria-label={menuAriaLabel}
              onClick={handleMenuOpen}
              sx={{ color: "var(--theme-text-secondary)", flexShrink: 0 }}
            >
              <MoreHoriz fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mt: 2,
            color: "var(--theme-text-secondary)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <AccessTime sx={{ fontSize: 16 }} aria-hidden />
            <Typography variant="caption" component="span">
              {formattedDate}
            </Typography>
          </Box>
          {session.userName ? (
            <Typography
              variant="caption"
              component="span"
              sx={{
                whiteSpace: "nowrap",
                maxWidth: 180,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {session.userName}
            </Typography>
          ) : null}
        </Box>
      </Box>

      <ChatHistoryCardMenu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        editTitleLabel={editTitleLabel}
        deleteLabel={deleteLabel}
        onClose={() => setMenuAnchorEl(null)}
        onEditTitle={() => onEditTitle(session)}
        onDelete={() => onDelete(session)}
      />
    </>
  );
}
